import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import $ from "jquery";

export default function AadhaarPayKycComponent({ onBack, onSuccess }) {
    const merchantdetails = useSelector((state) => state.auth?.employeeDetails);

    const username = merchantdetails?.username;

    const { captureFingerprint, loading: rdLoading } = useRDService();

    // ── APIs (Aadhaar Pay specific) ──
    const { callApi: checkEkycApi } = useApi(
        `/v1/fingpay/ekyc-status/${username || ""}`,
        "GET"
    );
    const { callApi: createMerchantApi } = useApi(
        "/v1/fingpay/merchant/create",
        "POST"
    );
    const { callApi: sendOtpApi } = useApi(
        "/v1/fingpay/ekyc/send-otp",
        "POST"
    );
    const { callApi: validateOtpApi } = useApi(
        "/v1/fingpay/ekyc/validate-otp",
        "POST"
    );
    const { callApi: resendOtpApi } = useApi(
        "/v1/fingpay/ekyc/resend-otp",
        "POST"
    );
    const { callApi: biometricApi } = useApi(
        "/v1/fingpay/ekyc/biometric",
        "POST"
    );

    // ── States ──
    const [loading, setLoading]                     = useState(false);
    const [message, setMessage]                     = useState("");
    const [otp, setOtp]                             = useState("");
    const [merchantData, setMerchantData]           = useState(null);
    const [showOnlyBio, setShowOnlyBio]             = useState(false);
    const [biometricCompleted, setBiometricCompleted] = useState(false);
    const [isBankVerification, setIsBankVerification] = useState(false);

    // ── Pre-fill from Redux ──
    const [form] = useState({
        name:    merchantdetails?.name || "",
        mobile:  merchantdetails?.mobile_number || "",
        email:   merchantdetails?.email || "",
        pan:     merchantdetails?.pan || "",
        aadhaar: merchantdetails?.aadhaar_number || "",
        dob:     merchantdetails?.dob || "",
        gender:  merchantdetails?.gender || "",
        address: merchantdetails?.address?.full || "",
        city:    merchantdetails?.address?.city || "",
        pincode: merchantdetails?.address?.pincode || "",
    });

    useEffect(() => {
        if (username) verifyEkycStatus();
    }, [username]);

    // ── eKYC status check ──
    const verifyEkycStatus = async () => {
        try {
            setLoading(true);
            const statusRes = await checkEkycApi(null);
            if (statusRes?.ekycDone === true) {
                setBiometricCompleted(true);
                setMessage("Merchant Activated Successfully");
                setMerchantData({ data: { merchantLoginId: statusRes?.mid } });
                setLoading(false);
                return;
            }
            await checkMerchantStatus();
        } catch {
            await checkMerchantStatus();
        }
    };

    const checkMerchantStatus = async () => {
        try {
            setLoading(true);
            const res = await createMerchantApi(null, `?username=${username}`);
            setMerchantData(res);
            if (
                (res?.statusCode === 10000 || res?.statusCode === 200) &&
                res?.data?.merchantStatus === true
            ) {
                setBiometricCompleted(true);
                setMessage("Merchant Activated Successfully");
            }
        } catch (error) {
            setMessage(error?.message || "Merchant check failed");
        } finally {
            setLoading(false);
        }
    };

    // ── PID XML Parser ──
    const parsePidXml = (xml) => {
        const $xml = $(xml);
        const get = (sel, attr) => $xml.find(sel).attr(attr) || "0";
        const infoVal = (name) =>
            $xml.find(`Info[name='${name}']`).attr("value") ||
            $xml.find(`Param[name='${name}']`).attr("value") ||
            "0";

        return {
            dc:          get("DeviceInfo", "dc"),
            ci:          get("Skey", "ci"),
            hmac:        $xml.find("Hmac").text() || "0",
            dpID:        get("DeviceInfo", "dpId"),
            mc:          get("DeviceInfo", "mc"),
            PidDatatype: get("Data", "type"),
            sessionKey:  $xml.find("Skey").text() || "0",
            mi:          get("DeviceInfo", "mi"),
            rdsID:       get("DeviceInfo", "rdsId"),
            errCode:     get("Resp", "errCode"),
            errInfo:     get("Resp", "errInfo"),
            fCount:      get("Resp", "fCount"),
            fType:       get("Resp", "fType"),
            iCount:      get("Resp", "iCount"),
            iType:       get("Resp", "iType"),
            pCount:      get("Resp", "pCount"),
            pType:       "0",
            srno:        infoVal("srno"),
            sysid:       infoVal("sysid"),
            ts:          infoVal("ts"),
            Piddata:     $xml.find("Data").text() || "0",
            qScore:      get("Resp", "qScore"),
            nmPoints:    get("Resp", "nmPoints"),
            rdsVer:      get("DeviceInfo", "rdsVer"),
        };
    };

    // ── OTP Handlers ──
    const handleSendOtp = async () => {
        try {
            setLoading(true);
            const res = await sendOtpApi(null, `?username=${username}`);
            setMessage(res?.statusCode === 10000 ? "OTP Sent Successfully" : res?.message || "OTP Send Failed");
        } catch (err) {
            setMessage(err?.message || "OTP send failed");
        } finally {
            setLoading(false);
        }
    };

    const handleValidateOtp = async () => {
        if (!otp) return setMessage("Please enter OTP");
        try {
            setLoading(true);
            const res = await validateOtpApi(null, `?username=${username}&otp=${otp}`);
            if (res?.statusCode === 10000) {
                setMessage("OTP Verified Successfully");
            } else if (res?.statusCode === 999) {
                setShowOnlyBio(true);
                setMessage("OTP Verified. Please complete biometric.");
            } else {
                setMessage(res?.message || "OTP Verification Failed");
            }
            setOtp("");
        } catch (err) {
            setMessage(err?.message || "OTP validation failed");
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        try {
            setLoading(true);
            const res = await resendOtpApi(null, `?username=${username}`);
            setMessage(res?.statusCode === 10000 ? "OTP Resent Successfully" : res?.message || "OTP resend failed");
        } catch (err) {
            setMessage(err?.message || "OTP resend failed");
        } finally {
            setLoading(false);
        }
    };

    // ── Biometric ──
    const handleBiometricSubmit = async () => {
        try {
            setLoading(true);
            const wadhValue = "E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc=";
            const pidXml = await captureFingerprint(wadhValue);
            if (!pidXml) return setMessage("Fingerprint capture failed");

            const payload = parsePidXml(pidXml);
            const res = await biometricApi(payload, `?username=${username}`);

            if (
                res?.statusCode === 10000 &&
                res?.message?.includes("Bank eKyc successfully completed")
            ) {
                setBiometricCompleted(true);
                setMessage("Aadhaar Pay eKYC successfully completed");
                verifyEkycStatus();
                if (onSuccess) setTimeout(() => onSuccess(), 1500);
                return;
            }

            if (
                res?.statusCode === 10000 &&
                (res?.message?.includes("Please complete Bank eKYC") ||
                    res?.message?.includes("Please Send & validate OTP before giving BioMetric") ||
                    res?.message === "SUCCESS")
            ) {
                setShowOnlyBio(false);
                setIsBankVerification(true);
                setMessage("Please verify OTP again");
                return;
            }

            setMessage(res?.message || "Biometric failed");
        } catch (err) {
            setMessage(err?.message || "Biometric failed");
        } finally {
            setLoading(false);
        }
    };

    // ── UI ──
    return (
        <div className="overflow-hidden bg-gray-100 p-4 flex items-center">
            <div className="w-full bg-white rounded-2xl shadow-xl p-6">

                {/* Header */}
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <button
                        onClick={onBack}
                        className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium"
                    >
                        ← Back to Aadhaar Pay
                    </button>
                </div>

                <h2 className="text-2xl font-semibold mb-6">
                    {isBankVerification ? "Bank Verification" : "Aadhaar Pay E-KYC"}
                </h2>

                {loading && (
                    <div className="text-center py-3 text-blue-600 font-medium">
                        Processing...
                    </div>
                )}

                {biometricCompleted ? (
                    <div className="text-center py-10">
                        <h3 className="text-2xl text-green-600 mb-6">
                            ✅ Merchant Activated Successfully
                        </h3>
                        <div className="bg-green-50 rounded-xl p-6">
                            <p className="text-gray-700">Aadhaar Pay onboarding completed successfully</p>
                            <p className="mt-2 font-semibold">
                                Merchant ID: {merchantData?.data?.merchantLoginId}
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* OTP input */}
                        {!showOnlyBio && (
                            <div className="flex gap-3 mb-4">
                                <input
                                    type="text"
                                    placeholder="Enter OTP"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    className="flex-1 border border-gray-300 rounded-xl px-4 py-3 outline-none"
                                />
                                <button
                                    onClick={handleValidateOtp}
                                    disabled={loading}
                                    className="bg-indigo-600 text-white px-5 rounded-xl disabled:opacity-50"
                                >
                                    Verify
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            {!showOnlyBio && (
                                <>
                                    <button
                                        onClick={handleSendOtp}
                                        disabled={loading}
                                        className="bg-[#12345A] hover:bg-[#12345A]/90 text-white py-3 rounded-xl font-medium transition-all disabled:opacity-50"
                                    >
                                        Send OTP
                                    </button>
                                    <button
                                        onClick={handleResendOtp}
                                        disabled={loading}
                                        className="border-2 border-[#12345A] text-[#12345A] hover:bg-slate-50 py-3 rounded-xl font-medium transition-all disabled:opacity-50"
                                    >
                                        Resend OTP
                                    </button>
                                </>
                            )}

                            <button
                                onClick={handleBiometricSubmit}
                                disabled={loading || rdLoading}
                                className="col-span-2 bg-[#12345A] hover:bg-[#12345A]/90 text-white py-3.5 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md mt-2"
                            >
                                {rdLoading ? "Capturing Fingerprint..." : "Complete Biometric eKYC"}
                            </button>
                        </div>
                    </>
                )}

                {message && (
                    <div className="mt-5 p-4 bg-gray-50 rounded-xl text-center text-sm text-gray-700">
                        {message}
                    </div>
                )}
            </div>
        </div>
    );
}
